import { Box } from '@mui/material';

import { Dialog, Text } from 'components';
import { formatDate, formatPrice } from 'core/shared';
import { replaceText, useTranslations } from 'hooks/useTranslations';

import { CustomString } from 'config/translations';
import { InvoiceBlock, InvoiceLine } from 'useQuery/types';

import {
  formatPeriod,
  formatSignedPrice,
  InvoiceStatusChip,
  useInvoiceCategoryLabel,
} from './InvoicingShared';

interface InvoiceLinesDialogProps {
  // null while the dialog closes, so the content keeps rendering during the
  // fade-out instead of collapsing to an empty box.
  invoice: InvoiceBlock | null;
  open: boolean;
  onClose: () => void;
}

const COLUMNS = {
  category: 108,
  description: 240,
  period: 148,
  amount: 144,
  amountInclVat: 144,
};

// Read-only breakdown of one invoice from GET
// /admin/organisation/{id}/invoices?show_scheduled=true. Lines of a scheduled
// invoice are projections and change when the settings above are saved.
function InvoiceLinesDialog({ invoice, open, onClose }: InvoiceLinesDialogProps) {
  const translations = useTranslations('organisationInvoicing');
  const categoryLabel = useInvoiceCategoryLabel();

  const lines = invoice?.lines ?? [];
  // Older API versions serve net amounts only; there is no rate on the line to
  // derive the rest from, so the incl. VAT column simply stays out.
  const hasInclVat = invoice?.gross_total !== undefined;
  const period = invoice ? formatPeriod(invoice.period_start, invoice.period_end) : '';
  // Scheduled rows have no Exact reference yet, so the period identifies them.
  const heading = invoice?.reference ?? (period || translations.noValue);
  const subHeading = invoice
    ? [
        formatDate({ date: invoice.invoice_date, variant: 'dayMonthYear' }),
        ...(invoice.reference && period ? [period] : []),
      ].join(' · ')
    : '';

  function HeaderCell({
    label,
    width,
    flex,
    alignRight,
  }: {
    label: CustomString;
    width: number;
    flex?: boolean;
    alignRight?: boolean;
  }) {
    return (
      <Box
        sx={{
          width,
          flex: flex ? 1 : undefined,
          minWidth: flex ? width : undefined,
          flexShrink: 0,
          textAlign: alignRight ? 'right' : undefined,
        }}
      >
        <Text.Bold>{label}</Text.Bold>
      </Box>
    );
  }

  function LineRow({ line }: { line: InvoiceLine }) {
    const period = formatPeriod(line.period_start, line.period_end);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 1,
          paddingY: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ width: COLUMNS.category, flexShrink: 0 }}>
          <Text.Small color="gray">{categoryLabel(line.category)}</Text.Small>
        </Box>
        <Box sx={{ flex: 1, minWidth: COLUMNS.description }}>
          <Text>{line.description}</Text>
          {!line.counts_as_revenue && (
            <Text.Small color="gray">{translations.exclRevenueTag}</Text.Small>
          )}
        </Box>
        <Box sx={{ width: COLUMNS.period, flexShrink: 0 }}>
          <Text color={period ? undefined : 'gray'}>{period || translations.noValue}</Text>
        </Box>
        <Box sx={{ width: COLUMNS.amount, flexShrink: 0, textAlign: 'right' }}>
          <Text color={line.amount_net < 0 ? 'success.main' : undefined} noWrap>
            {formatSignedPrice(line.amount_net)}
          </Text>
        </Box>
        {hasInclVat && (
          <Box sx={{ width: COLUMNS.amountInclVat, flexShrink: 0, textAlign: 'right' }}>
            <Text color={(line.amount_gross ?? 0) < 0 ? 'success.main' : undefined} noWrap>
              {line.amount_gross === undefined
                ? translations.noValue
                : formatSignedPrice(line.amount_gross)}
            </Text>
          </Box>
        )}
      </Box>
    );
  }

  function TotalRow({
    label,
    value,
    bold,
  }: {
    label: CustomString;
    value: number;
    bold?: boolean;
  }) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          paddingY: 0.5,
        }}
      >
        {bold ? <Text.Bold>{label}</Text.Bold> : <Text color="gray">{label}</Text>}
        {bold ? (
          <Text.Bold>{formatPrice({ price: value, symbol: true })}</Text.Bold>
        ) : (
          <Text>{formatPrice({ price: value, symbol: true })}</Text>
        )}
      </Box>
    );
  }

  return (
    <Dialog
      title={translations.linesDialogTitle}
      open={open}
      onChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth
      footer={[
        {
          variant: 'outlined',
          label: translations.close,
          onClick: onClose,
        },
      ]}
    >
      <Box>
        {!!invoice && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              paddingBottom: 2,
            }}
          >
            <InvoiceStatusChip status={invoice.status} />
            <Box>
              <Text.Bold>{heading}</Text.Bold>
              <Text.Small color="gray">{subHeading}</Text.Small>
            </Box>
            <Box sx={{ flex: 1 }} />
            <Text.Small color="gray">
              {lines.length === 1
                ? translations.linesCountOne
                : replaceText({
                    text: translations.linesCountMany,
                    replaceValues: [lines.length],
                  })}
            </Text.Small>
          </Box>
        )}

        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: hasInclVat ? 768 : 616 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
                paddingY: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <HeaderCell label={translations.categoryHeader} width={COLUMNS.category} />
              <HeaderCell label={translations.descriptionHeader} width={COLUMNS.description} flex />
              <HeaderCell label={translations.periodHeader} width={COLUMNS.period} />
              <HeaderCell label={translations.lineAmountHeader} width={COLUMNS.amount} alignRight />
              {hasInclVat && (
                <HeaderCell
                  label={translations.lineAmountInclVatHeader}
                  width={COLUMNS.amountInclVat}
                  alignRight
                />
              )}
            </Box>

            {lines.length === 0 && (
              <Box sx={{ paddingY: 2 }}>
                <Text color="gray">{translations.noLines}</Text>
              </Box>
            )}

            {lines.map((line, index) => (
              <LineRow key={line.item_id ?? `${line.category}-${index}`} line={line} />
            ))}
          </Box>
        </Box>

        {!!invoice && (
          <Box sx={{ paddingTop: 1.5 }}>
            <TotalRow label={translations.subscriptionHeader} value={invoice.subscription_net} />
            <TotalRow label={translations.usageHeader} value={invoice.usage_net} />
            <TotalRow label={translations.otherHeader} value={invoice.other_net} />
            <Box sx={{ paddingTop: 1, marginTop: 1, borderTop: 1, borderColor: 'divider' }}>
              <TotalRow label={translations.totalHeader} value={invoice.net_total} bold />
              {invoice.gross_total !== undefined && (
                <TotalRow
                  label={translations.totalInclVatHeader}
                  value={invoice.gross_total}
                  bold
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}

export { InvoiceLinesDialog };
